/**
 * useSwipeGesture — small reusable touch swipe hook.
 *
 * Listens for `touchstart` / `touchend` on the supplied element ref and
 * dispatches one of `onSwipeLeft|Right|Up|Down` when the gesture
 * exceeds `threshold` pixels along the dominant axis. The hook is
 * intentionally conservative so it never fights the browser's native
 * scroll/text-selection gestures (Requirement 9.5):
 *
 *   - Only single-finger swipes count (`e.touches.length === 1`).
 *   - Listeners use `{ passive: true }` so the browser is free to
 *     scroll while the user is dragging — we do not call
 *     `preventDefault()`.
 *   - We compare absolute `dx` vs. `dy` and only fire on the larger
 *     axis, so a near-vertical drag never accidentally counts as a
 *     horizontal swipe.
 *
 * Validates: Requirement 9.5 (touch gestures for sidebar / modal
 * dismiss without conflicting with text selection).
 */

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

export interface SwipeOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeDown?: () => void;
  onSwipeUp?: () => void;
  /** Minimum px distance to count as a swipe. Defaults to 50. */
  threshold?: number;
  /** Disable the hook (e.g. when the gesture's owner is unmounted). */
  enabled?: boolean;
}

interface SwipeStart {
  x: number;
  y: number;
  t: number;
}

/**
 * Bind the configured swipe handlers to `target.current`. Re-binds
 * whenever any handler reference changes; the consumer is responsible
 * for memoizing handlers if rebind churn is undesirable.
 */
export function useSwipeGesture(
  target: RefObject<HTMLElement | null>,
  opts: SwipeOptions,
): void {
  const startRef = useRef<SwipeStart | null>(null);
  // Stash the latest opts in a ref so the listeners always read the
  // freshest handlers without needing every callback to be memoized
  // by the caller.
  const optsRef = useRef<SwipeOptions>(opts);
  optsRef.current = opts;

  useEffect(() => {
    const el = target.current;
    if (!el) return;
    if (opts.enabled === false) return;

    const handleStart = (e: TouchEvent): void => {
      if (e.touches.length !== 1) {
        startRef.current = null;
        return;
      }
      const t = e.touches[0];
      if (!t) return;
      startRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    };

    const handleEnd = (e: TouchEvent): void => {
      const start = startRef.current;
      startRef.current = null;
      if (!start) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      const threshold = optsRef.current.threshold ?? 50;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      // Pick the dominant axis so a diagonal flick does not double-fire.
      if (absX > absY) {
        if (dx > threshold) optsRef.current.onSwipeRight?.();
        else if (dx < -threshold) optsRef.current.onSwipeLeft?.();
      } else {
        if (dy > threshold) optsRef.current.onSwipeDown?.();
        else if (dy < -threshold) optsRef.current.onSwipeUp?.();
      }
    };

    const handleCancel = (): void => {
      startRef.current = null;
    };

    el.addEventListener('touchstart', handleStart, { passive: true });
    el.addEventListener('touchend', handleEnd, { passive: true });
    el.addEventListener('touchcancel', handleCancel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', handleStart);
      el.removeEventListener('touchend', handleEnd);
      el.removeEventListener('touchcancel', handleCancel);
    };
  }, [target, opts.enabled]);
}
